import { useEffect, useReducer, useRef, useState } from 'react';
import * as Comlink from 'comlink';
import type { PipelineApi } from '../workers/pipeline.worker';
import type { Mode, Params, PipelineMetrics } from './types';
import { defaultParams, paramsReducer } from './params';
import { loadParams as loadStoredParams, saveParams } from './storage';

export interface VektorStore {
  params: Params;
  setParam: <K extends keyof Params>(key: K, value: Params[K]) => void;
  resetParams: () => void;
  setMode: (mode: Mode) => void;
  loadFile: (file: File) => Promise<void>;
  clearFile: () => void;
  loadParams: (p: Params) => void;
  metrics: PipelineMetrics | null;
  svg: string | null;
  histogram: number[];
  sourceLoaded: boolean;
  isProcessing: boolean;
}

export function useVektorStore(): VektorStore {
  const [params, dispatch] = useReducer(paramsReducer, undefined, () => loadStoredParams('bw') ?? defaultParams());
  const [svg, setSvg] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<PipelineMetrics | null>(null);
  const [histogram, setHistogram] = useState<number[]>(() => new Array(256).fill(0));
  const [sourceLoaded, setSourceLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const apiRef = useRef<Comlink.Remote<PipelineApi> | null>(null);
  const draggingRef = useRef(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const paramsRef = useRef(params);
  const sourceLoadedRef = useRef(sourceLoaded);
  paramsRef.current = params;
  sourceLoadedRef.current = sourceLoaded;

  useEffect(() => {
    const worker = new Worker(new URL('../workers/pipeline.worker.ts', import.meta.url), { type: 'module' });
    apiRef.current = Comlink.wrap<PipelineApi>(worker);
    return () => worker.terminate();
  }, []);

  useEffect(() => saveParams(params.mode, params), [params]);

  useEffect(() => {
    if (!sourceLoaded || !apiRef.current) return;
    window.clearTimeout(debounceRef.current);
    setIsProcessing(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const result = await apiRef.current!.run(params, { lowRes: draggingRef.current });
        setSvg(result.svg);
        setMetrics(result.metrics);
      } catch (err) {
        console.error('[vektor] pipeline run failed', err);
      } finally {
        setIsProcessing(false);
      }
    }, 150);
    return () => window.clearTimeout(debounceRef.current);
  }, [params, sourceLoaded]);

  useEffect(() => {
    const onPointerDown = () => { draggingRef.current = true; };
    const onPointerUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      if (!apiRef.current || !sourceLoadedRef.current) return;
      window.clearTimeout(debounceRef.current);
      setIsProcessing(true);
      apiRef.current.run(paramsRef.current, { lowRes: false }).then((r) => {
        setSvg(r.svg);
        setMetrics(r.metrics);
      }).catch((err) => {
        console.error('[vektor] pipeline run failed', err);
      }).finally(() => setIsProcessing(false));
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  return {
    params,
    setParam: (key, value) => dispatch({ type: 'SET', key, value }),
    resetParams: () => dispatch({ type: 'RESET' }),
    setMode: (mode) => dispatch({ type: 'SET_MODE', mode, loaded: loadStoredParams(mode) ?? { ...defaultParams(), mode } }),
    loadParams: (p: Params) => dispatch({ type: 'LOAD', params: p }),
    loadFile: async (file) => {
      if (!apiRef.current) throw new Error('Worker not ready');
      const info = await apiRef.current.loadFile(file, params.retraceSvg);
      setHistogram(info.histogram);
      setSourceLoaded(true);
    },
    clearFile: () => {
      window.clearTimeout(debounceRef.current);
      setSourceLoaded(false);
      setSvg(null);
      setMetrics(null);
      setHistogram(new Array(256).fill(0));
      setIsProcessing(false);
    },
    metrics,
    svg,
    histogram,
    sourceLoaded,
    isProcessing,
  };
}
